import {maxImagesOnListing, requiredListingFields} from "@hosttools/core/constant";
import type {
    ListingImage,
    ListingKeyCollection,
    ListingNoteMarketingNameText
} from "@hosttools/core/generated/models";
import buildChannels from "@hosttools/core/shared/buildChannels";
import {
    Box,
    Button,
    ButtonSpinner,
    ButtonText,
    ChannelLogo,
    Checkbox,
    CheckboxIcon,
    CheckboxIndicator,
    CheckboxLabel,
    ChevronDownIcon,
    Dropdown,
    DropdownMenuItem,
    Form,
    FormField,
    FormGroup,
    HStack,
    Input,
    InputField,
    SelectGeneric,
    useAbortController,
    useCheckInOutTimes,
    useDefaultTrigger,
    useListingService,
    useToast,
    VStack
} from "@hosttools/frontend";
import FormUploadFile from "@hosttools/frontend/components/FormUploadFile";
import type {AccountChannelType} from "@hosttools/frontend/models/account";
import type {AttachmentOrFileType, FileType} from "@hosttools/frontend/models/attachment";
import {
    defaultCheckInHour,
    defaultCheckOutHour,
    type Listing
} from "@hosttools/frontend/models/listing";
import merge from "lodash/merge";
import type React from "react";
import type {ComponentRef} from "react";
import {memo, useCallback, useEffect, useMemo, useRef, useState} from "react";
import type {SubmitHandler} from "react-hook-form";
import {Controller, FormProvider, useController, useForm} from "react-hook-form";

import AttributesWithQuantity from "./AttributesWithQuantity";
import BedroomsConfiguration from "./BedroomsConfiguration";
import {
    allowedPetOptions,
    availableOfInternetOptions,
    bookingLeadTimeOptions,
    checkinMethodOptions,
    currencyOptions,
    kindOfInternetOptions,
    locatedParkingOptions,
    propertyTypeOptions,
    reservationParkingOptions,
    spaceOptions,
    tagOptions,
    timeCostParkingOptions
} from "./constant";
import Fees from "./Fees";
import ListingSettings from "./ListingSettings";
import Location from "./Location";
import NearbyAmenitie from "./NearbyAmenitie";
import Taxes from "./Taxes";
import TextAreaForm from "./TextAreaForm";
import type {ListingAttachmentOrFileType, ListingFormType} from "./type";
import useListingObjectFromChannel from "./useListingObjectFromChannel";

import Tabs from "@/client/components/Tabs";
import Tab from "@/client/components/Tabs/Tab";

type FormUploadFileRef = React.ComponentRef<typeof FormUploadFile>;
type Props = {
    isSimple: boolean;
    listing: Listing | undefined | null;
    onCancel: () => void;
    onSaveListingSetting?: () => void;
} & StrictUnion<
    | {
          autoConnectToCC?: false;
          onSave: (listingID: string) => void;
      }
    | {
          autoConnectToCC: true;
          onSave: (listingID: string, accountID: string) => void;
      }
>;

const requiredFieldsChannelConnector = [
    "airbnbName",
    "persons",
    "bathrooms",
    "keyCollection.checkInMethod",
    "keyCollection.additionalInfo.instruction.how",
    "keyCollection.additionalInfo.instruction.when",
    "policy.petPolicy.allowedPets",
    "propertyType",
    "currency",
    "airbnbCheckInTime",
    "airbnbCheckOutTime"
] as const satisfies readonly FlattenObjectKeys<DeepRequired<Listing>>[];

const ListingForm: React.FC<Props> = ({
    listing: listingProp,
    isSimple = true,
    autoConnectToCC,
    onCancel,
    onSaveListingSetting,
    onSave
}) => {
    const toast = useToast();
    const [tab, setTab] = useState<"settings" | (string & {})>("settings");
    // this state is used to lock the listing from updates in case of form dirty
    const [listing, setListing] = useState(listingProp);
    const [channel, setChannel] = useState<AccountChannelType>();
    const listingService = useListingService();
    const {listing: listingDataFromChannel, isLoading} = useListingObjectFromChannel({
        listingID: listing?._id,
        channel
    });
    const methods = useForm<ListingFormType>({
        defaultValues: listing ?? {
            currency: "USD",
            airbnbCheckInTime: defaultCheckInHour,
            airbnbCheckOutTime: defaultCheckOutHour
        }
    });
    const requiredFields = autoConnectToCC ? requiredFieldsChannelConnector : requiredListingFields;
    const formRef = useRef<React.ComponentRef<typeof Form<ListingFormType>>>(null);

    const {
        control,
        formState: {errors: formErrors, isDirty, submitCount},
        setValue,
        setError,
        register,
        reset,
        handleSubmit,
        watch
    } = methods;
    const {
        field: {value: currency}
    } = useController({control, name: "currency"});
    const kindOfInternet = watch("policy.internetPolicy.kindOfInternet");

    const uploadImageRef = useRef<FormUploadFileRef>(null);
    const [loading, setLoading] = useState(false);
    const checkInOutOptions = useCheckInOutTimes();
    const abortRef = useAbortController();
    const listingSettingsRef = useRef<ComponentRef<typeof ListingSettings>>(null);
    const shouldFocusListingNameField = useRef(false);

    // still allow to receive new updates if the form isn't dirty yet
    useEffect(() => {
        if (!isDirty) {
            setListing(listingProp);
        }
    }, [isDirty, listingProp]);

    useEffect(() => {
        if (listingDataFromChannel) {
            // we might need to delete the existing images from the listing
            // if user decides to save it
            reset(merge(listing, listingDataFromChannel));
        }
    }, [listingDataFromChannel, listing, reset]);

    const validateNumber = useCallback((value: string | number | undefined) => {
        if (value && Number.isNaN(Number(value))) {
            return "Please enter number.";
        }

        if (value && Number(value) < 0) {
            return "This must be greater than 0";
        }

        return true;
    }, []);

    const fieldImagesRef = useRef<React.ComponentRef<typeof FormField>>(null);
    const handleUploadError = useCallback(
        (type: "maxImages") => {
            if (type === "maxImages") {
                setError("images", {
                    message: `The maximum number of images is ${maxImagesOnListing}`
                });
                // scroll to the error element to see the error
                fieldImagesRef.current?.scrollIntoError();
            }
        },
        [setError]
    );

    const submit: SubmitHandler<ListingFormType> = useCallback(
        async fields => {
            if (!uploadImageRef.current) {
                return;
            }
            try {
                setLoading(true);
                const rooms = fields.bedroomConfiguration?.bedrooms ?? [];
                const {bedrooms, livingRoom} = rooms.reduce(
                    (arr, room) => {
                        if (room.type === "Bedroom") {
                            arr.bedrooms += 1;
                        } else {
                            arr.livingRoom += 1;
                        }
                        return arr;
                    },
                    {
                        bedrooms: 0,
                        livingRoom: 0
                    }
                );
                let _id: string | undefined;
                if (!listing) {
                    const {images: _, channels, ...rest} = fields;
                    const payload = {
                        ...rest,
                        bedrooms,
                        livingRoom,
                        totalBeds: bedrooms + livingRoom,
                        hidden: false,
                        bathrooms: fields.bathrooms ?? 0
                    };
                    const {_id: id} = await listingService.post("/addListingContent", {
                        listing: payload,
                        isAutoConnectToCC: !!autoConnectToCC
                    });
                    const images = await uploadImageRef.current.save(id);
                    if (images?.length) {
                        await listingService.post("/addListingContent", {
                            listing: {
                                ...payload,
                                _id: id,
                                airbnbThumbnailUrl: images[0].url,
                                ...(images && {images: images as ListingImage[]})
                            },
                            isAutoConnectToCC: !!autoConnectToCC
                        });
                    }
                    _id = id;
                } else {
                    const images = await uploadImageRef.current.save(listing._id);
                    const airbnbThumbnailUrl = images?.[0]?.url ?? listing.images?.[0].url;
                    const {channels, ...rest} = fields;
                    const {_id: id} = await listingService.post("/addListingContent", {
                        listing: {
                            ...rest,
                            airbnbThumbnailUrl,
                            bathrooms: fields.bathrooms ?? 0,
                            bedrooms,
                            livingRoom,
                            totalBeds: bedrooms + livingRoom,
                            images: images as ListingImage[]
                        },
                        isAutoConnectToCC: !!autoConnectToCC
                    });
                    _id = id;
                }
                if (_id) {
                    if (autoConnectToCC) {
                        if (!listing?.channels.ChannelConnector) {
                            const {accountID} = await listingService.post(
                                "/createListingOnChannel",
                                {
                                    listingID: _id,
                                    channel: "ChannelConnector"
                                }
                            );
                            onSave(_id, accountID);
                        }
                    } else {
                        onSave(_id);
                    }
                }
                toast.success("Listing content updated");
            } finally {
                setLoading(false);
            }
        },
        [listing, toast, listingService, autoConnectToCC, onSave]
    );

    const handleValidateDescription = useCallback((value?: ListingNoteMarketingNameText[]) => {
        if (!value) {
            return "Please enter description.";
        }

        const description = value.find(item => item.language === "en");

        if (!description?.value || description.value.trim() === "") {
            return "Please enter description.";
        }

        return true;
    }, []);

    // still permit form reset if no edits have been made
    useEffect(() => {
        if (listing?._id && !isDirty) {
            reset(listing);
        }
    }, [listing, isDirty, reset]);

    useEffect(() => {
        if (listing?.images) {
            setValue("images", listing.images);
        }
    }, [listing?.images, setValue]);

    const setKeyCollection = useCallback(
        (value: ListingKeyCollection["checkInMethod"]) => {
            setValue("keyCollection.type", "primary", {shouldDirty: true});
            setValue("keyCollection.checkInMethod", value);
        },
        [setValue]
    );

    const handleImagesChange = useCallback(
        (values: AttachmentOrFileType[]) => {
            setValue("images", values as ListingAttachmentOrFileType[], {shouldDirty: true});
        },
        [setValue]
    );

    const handleUploadImages = useCallback(
        async (
            listingID: string,
            file: FileType,
            onUploadProgress?: (file: FileType, value: number) => void
        ) => {
            return listingService.uploadImage(
                {
                    listingID,
                    file: file.resize,
                    filename: file.file.name
                },
                {
                    signal: abortRef.current?.signal,
                    ...(onUploadProgress && {
                        onUploadProgress: evt => {
                            if (evt.total) {
                                const progress = Math.round((evt.loaded * 100) / evt.total);
                                onUploadProgress(file, progress);
                            }
                        }
                    })
                }
            );
        },
        [abortRef, listingService]
    );

    const handleDeleteImages = useCallback(
        async (keys: string[]) => {
            await listingService.deleteImages(
                {keys},
                {
                    signal: abortRef.current?.signal
                }
            );
        },
        [abortRef, listingService]
    );

    const selectableChannels = useMemo(() => {
        if (!listing) {
            return [];
        }
        return buildChannels(listing).filter(
            elem => elem !== "ChannelConnector" && elem !== "direct"
        );
    }, [listing]);

    const triggerImportData = useDefaultTrigger(ChevronDownIcon, false, {
        text: "Import data",
        variant: "outline",
        action: "secondary",
        isLoading
    });

    const handleSubmitForm = useCallback(async () => {
        if (tab === "settings") {
            try {
                setLoading(true);
                await listingSettingsRef.current?.submit({
                    onError: error => {
                        if (error === "has_errors") {
                            toast.error("Please fix all validation errors before saving");
                        } else if (error === "not_dirty") {
                            toast.success("Listing content updated");
                        }
                    }
                });
            } finally {
                setLoading(false);
            }
        } else {
            if (!isDirty) {
                toast.success("Listing content updated");
                return;
            }
            await handleSubmit(submit, () => {
                toast.error("Please fix all validation errors before saving");
            })();
        }
    }, [tab, handleSubmit, submit, isDirty, toast]);

    const handleChangeTab = useCallback(
        (tab: string) => {
            setTab(tab);
            if (shouldFocusListingNameField.current && tab === "property") {
                methods.setFocus("airbnbName");
                shouldFocusListingNameField.current = false;
            }
        },
        [methods]
    );

    const handleEditListingName = useCallback(() => {
        shouldFocusListingNameField.current = true;
        formRef.current?.scrollToField("airbnbName");
    }, []);

    return (
        <FormProvider {...methods}>
            <Form
                ref={formRef}
                autoScrollToErrors={tab !== "settings"}
                errors={formErrors}
                submitCount={submitCount}
            >
                <VStack space="lg">
                    {selectableChannels.length > 0 && (
                        <Box className="self-end">
                            <Dropdown placement="bottom right" trigger={triggerImportData}>
                                {selectableChannels.map(elem => (
                                    <DropdownMenuItem
                                        key={elem}
                                        onPress={() => {
                                            if (elem === "direct") {
                                                return;
                                            }
                                            setChannel(elem);
                                        }}
                                    >
                                        <ChannelLogo includeLabel channel={elem} />
                                    </DropdownMenuItem>
                                ))}
                            </Dropdown>
                        </Box>
                    )}
                    <Tabs
                        withForm
                        defaultTab={listing ? "settings" : "property"}
                        variant="tab"
                        onChange={handleChangeTab}
                    >
                        {listing && (
                            <Tab id="settings" title="Settings">
                                <ListingSettings
                                    ref={listingSettingsRef}
                                    channels={selectableChannels}
                                    listing={listing}
                                    onEditListingName={handleEditListingName}
                                    onSubmitSuccess={onSaveListingSetting}
                                />
                            </Tab>
                        )}
                        <Tab id="property" title="Property Details">
                            <VStack space="lg">
                                <FormField
                                    isInsideTab
                                    error={formErrors.airbnbName?.message}
                                    label="Listing Name"
                                    name="airbnbName"
                                >
                                    <Controller
                                        control={control}
                                        name="airbnbName"
                                        render={({field}) => {
                                            return (
                                                <Input>
                                                    <InputField
                                                        id="name"
                                                        placeholder="Name..."
                                                        testID="name"
                                                        type="text"
                                                        value={field.value ?? ""}
                                                        onBlur={field.onBlur}
                                                        onChange={field.onChange}
                                                    />
                                                </Input>
                                            );
                                        }}
                                        rules={{
                                            required: requiredFields.includes("airbnbName")
                                                ? "Please enter name."
                                                : false
                                        }}
                                    />
                                </FormField>
                                {!isSimple && (
                                    <>
                                        <HStack space="lg">
                                            <FormField
                                                isInsideTab
                                                error={formErrors.space?.message}
                                                label="Listing size"
                                                name="space"
                                            >
                                                <Input>
                                                    <InputField
                                                        id="space"
                                                        placeholder="Amount..."
                                                        testID="space"
                                                        type="number"
                                                        {...register("space", {
                                                            required: false,
                                                            validate: validateNumber
                                                        })}
                                                    />
                                                </Input>
                                            </FormField>
                                            <FormField
                                                isInsideTab
                                                error={formErrors.spaceUnit?.message}
                                                label="Units"
                                                name="spaceUnit"
                                            >
                                                <Controller
                                                    control={control}
                                                    name="spaceUnit"
                                                    render={({field}) => {
                                                        return (
                                                            <SelectGeneric
                                                                options={spaceOptions}
                                                                placeholder="Space unit..."
                                                                value={field.value}
                                                                onChange={field.onChange}
                                                            />
                                                        );
                                                    }}
                                                />
                                            </FormField>
                                        </HStack>
                                        <VStack className="md:flex-row" space="lg">
                                            <FormField
                                                isInsideTab
                                                error={formErrors.persons?.message}
                                                label="Adults"
                                                name="persons"
                                            >
                                                <Input>
                                                    <InputField
                                                        id="persons"
                                                        placeholder="Adults..."
                                                        testID="persons"
                                                        type="number"
                                                        {...register("persons", {
                                                            required: requiredFields.includes(
                                                                "persons"
                                                            )
                                                                ? "Please enter number of adults."
                                                                : false,
                                                            validate: validateNumber
                                                        })}
                                                    />
                                                </Input>
                                            </FormField>
                                            <FormField
                                                isInsideTab
                                                error={formErrors.childs?.message}
                                                label="Children"
                                                name="childs"
                                            >
                                                <Input>
                                                    <InputField
                                                        id="childs"
                                                        placeholder="Number of children..."
                                                        testID="childs"
                                                        type="number"
                                                        {...register("childs", {
                                                            required: false,
                                                            validate: validateNumber
                                                        })}
                                                    />
                                                </Input>
                                            </FormField>
                                            <FormField
                                                isInsideTab
                                                error={formErrors.livingRoom?.message}
                                                label="Living Rooms"
                                                name="livingRoom"
                                            >
                                                <Input>
                                                    <InputField
                                                        id="livingRoom"
                                                        placeholder="Number of living rooms..."
                                                        testID="livingRoom"
                                                        type="number"
                                                        {...register("livingRoom", {
                                                            required: false,
                                                            validate: validateNumber
                                                        })}
                                                    />
                                                </Input>
                                            </FormField>
                                            <FormField
                                                isInsideTab
                                                error={formErrors.bathrooms?.message}
                                                label="Bathrooms"
                                                name="bathrooms"
                                            >
                                                <Input>
                                                    <InputField
                                                        id="bathrooms"
                                                        placeholder="Number of bathrooms..."
                                                        testID="bathrooms"
                                                        type="number"
                                                        {...register("bathrooms", {
                                                            required: requiredFields.includes(
                                                                "bathrooms"
                                                            )
                                                                ? "Please enter number of bathrooms."
                                                                : false,
                                                            validate: validateNumber,
                                                            min: {
                                                                value: 1,
                                                                message:
                                                                    "This number should be greater than 0"
                                                            }
                                                        })}
                                                    />
                                                </Input>
                                            </FormField>
                                            <FormField
                                                isInsideTab
                                                error={formErrors.toilets?.message}
                                                label="Toilets"
                                                name="toilets"
                                            >
                                                <Input>
                                                    <InputField
                                                        id="toilets"
                                                        placeholder="Number of toilets..."
                                                        testID="toilets"
                                                        type="number"
                                                        {...register("toilets", {
                                                            required: false,
                                                            validate: validateNumber
                                                        })}
                                                    />
                                                </Input>
                                            </FormField>
                                        </VStack>
                                    </>
                                )}
                                <FormField
                                    isInsideTab
                                    error={formErrors.notes?.description?.texts?.message}
                                    label="Description"
                                    name="notes.description.texts"
                                >
                                    <Controller
                                        control={control}
                                        name="notes.description.texts"
                                        render={({field}) => {
                                            return (
                                                <TextAreaForm
                                                    isArray
                                                    isInvalid={
                                                        !!formErrors.notes?.description?.texts
                                                    }
                                                    languageTexts={field.value}
                                                    onChange={field.onChange}
                                                />
                                            );
                                        }}
                                        rules={{
                                            validate: handleValidateDescription
                                        }}
                                    />
                                </FormField>
                                {!isSimple && (
                                    <>
                                        <FormField
                                            isInsideTab
                                            label="House rules"
                                            name="notes.houseRules.texts"
                                        >
                                            <Controller
                                                control={control}
                                                name="notes.houseRules.texts"
                                                render={({field}) => {
                                                    return (
                                                        <TextAreaForm
                                                            isArray
                                                            languageTexts={field.value}
                                                            onChange={field.onChange}
                                                        />
                                                    );
                                                }}
                                            />
                                        </FormField>
                                        <FormField
                                            isInsideTab
                                            label="Fine print"
                                            name="notes.finePrint.texts"
                                        >
                                            <Controller
                                                control={control}
                                                name="notes.finePrint.texts"
                                                render={({field}) => {
                                                    return (
                                                        <TextAreaForm
                                                            isArray
                                                            languageTexts={field.value}
                                                            onChange={field.onChange}
                                                        />
                                                    );
                                                }}
                                            />
                                        </FormField>
                                        <FormField
                                            isInsideTab
                                            label="Short description"
                                            name="notes.shortDescription.texts"
                                        >
                                            <Controller
                                                control={control}
                                                name="notes.shortDescription.texts"
                                                render={({field}) => {
                                                    return (
                                                        <TextAreaForm
                                                            isArray
                                                            languageTexts={field.value}
                                                            onChange={field.onChange}
                                                        />
                                                    );
                                                }}
                                            />
                                        </FormField>
                                        <FormField
                                            isInsideTab
                                            label="Notes name"
                                            name="notes.name.texts"
                                        >
                                            <Controller
                                                control={control}
                                                name="notes.name.texts"
                                                render={({field}) => {
                                                    return (
                                                        <TextAreaForm
                                                            isArray
                                                            languageTexts={field.value}
                                                            onChange={field.onChange}
                                                        />
                                                    );
                                                }}
                                            />
                                        </FormField>
                                        <FormField label="Marketing name">
                                            <Controller
                                                control={control}
                                                name="notes.marketingName.texts"
                                                render={({field}) => {
                                                    return (
                                                        <TextAreaForm
                                                            isArray
                                                            languageTexts={field.value}
                                                            onChange={field.onChange}
                                                        />
                                                    );
                                                }}
                                            />
                                        </FormField>
                                    </>
                                )}
                                <HStack space="lg">
                                    <FormField
                                        isInsideTab
                                        error={formErrors.propertyType?.message}
                                        label="Property Type"
                                        name="propertyType"
                                    >
                                        <Controller
                                            control={control}
                                            name="propertyType"
                                            render={({field}) => {
                                                return (
                                                    <SelectGeneric
                                                        options={propertyTypeOptions}
                                                        placeholder="Select..."
                                                        value={field.value}
                                                        onChange={field.onChange}
                                                    />
                                                );
                                            }}
                                            rules={{
                                                required: requiredFields.includes("propertyType")
                                                    ? "Please select property type"
                                                    : false
                                            }}
                                        />
                                    </FormField>
                                    <FormField
                                        isInsideTab
                                        error={formErrors.currency?.message}
                                        label="Currency"
                                        name="currency"
                                    >
                                        <Controller
                                            control={control}
                                            name="currency"
                                            render={({field}) => {
                                                return (
                                                    <SelectGeneric
                                                        options={currencyOptions}
                                                        placeholder="Currency..."
                                                        value={field.value}
                                                        onChange={field.onChange}
                                                    />
                                                );
                                            }}
                                            rules={{
                                                required: requiredFields.includes("currency")
                                                    ? "Please select currency"
                                                    : false
                                            }}
                                        />
                                    </FormField>
                                </HStack>
                                <HStack className="flex-wrap" space="lg">
                                    <FormField
                                        isInsideTab
                                        error={formErrors.airbnbCheckInTime?.message}
                                        label="Check-in time"
                                        name="airbnbCheckInTime"
                                    >
                                        <Controller
                                            control={control}
                                            name="airbnbCheckInTime"
                                            render={({field}) => {
                                                return (
                                                    <SelectGeneric
                                                        options={checkInOutOptions}
                                                        placeholder="Check-in time"
                                                        value={field.value}
                                                        onChange={field.onChange}
                                                    />
                                                );
                                            }}
                                            rules={{
                                                required: requiredFields.includes(
                                                    "airbnbCheckInTime"
                                                )
                                                    ? "Please select check-in time"
                                                    : false,
                                                validate: (value, {airbnbCheckOutTime}) => {
                                                    if (
                                                        typeof value === "number" &&
                                                        typeof airbnbCheckOutTime === "number" &&
                                                        value < airbnbCheckOutTime
                                                    ) {
                                                        return "Check-in time should always greater than check-out time";
                                                    }
                                                    return true;
                                                }
                                            }}
                                        />
                                    </FormField>
                                    <FormField
                                        isInsideTab
                                        error={formErrors.airbnbCheckOutTime?.message}
                                        label="Check-out time"
                                        name="airbnbCheckOutTime"
                                    >
                                        <Controller
                                            control={control}
                                            name="airbnbCheckOutTime"
                                            render={({field}) => {
                                                return (
                                                    <SelectGeneric
                                                        options={checkInOutOptions}
                                                        placeholder="Check-out time"
                                                        value={field.value}
                                                        onChange={field.onChange}
                                                    />
                                                );
                                            }}
                                            rules={{
                                                required: requiredFields.includes(
                                                    "airbnbCheckOutTime"
                                                )
                                                    ? "Please select check-out time"
                                                    : false
                                            }}
                                        />
                                    </FormField>
                                    <FormField label="Booking lead time">
                                        <Controller
                                            control={control}
                                            name="leadTime"
                                            render={({field}) => {
                                                return (
                                                    <SelectGeneric
                                                        options={bookingLeadTimeOptions}
                                                        placeholder="Booking lead time..."
                                                        value={field.value}
                                                        onChange={field.onChange}
                                                    />
                                                );
                                            }}
                                        />
                                    </FormField>
                                </HStack>
                                <Location />
                                <FormGroup label="Key collection">
                                    <HStack className="flex-wrap" space="lg">
                                        <FormField
                                            isInsideTab
                                            error={formErrors.keyCollection?.checkInMethod?.message}
                                            label="Check-in method"
                                            name="keyCollection.checkInMethod"
                                        >
                                            <Controller
                                                control={control}
                                                name="keyCollection.checkInMethod"
                                                render={({field}) => {
                                                    return (
                                                        <SelectGeneric
                                                            options={checkinMethodOptions}
                                                            placeholder="Method..."
                                                            value={field.value}
                                                            onChange={setKeyCollection}
                                                        />
                                                    );
                                                }}
                                                rules={{
                                                    required: requiredFields.includes(
                                                        "keyCollection.checkInMethod"
                                                    )
                                                        ? "Please select check-in method"
                                                        : false
                                                }}
                                            />
                                        </FormField>
                                        <FormField
                                            isInsideTab
                                            error={
                                                formErrors.keyCollection?.additionalInfo
                                                    ?.instruction?.how?.message
                                            }
                                            label="How key should be collected"
                                            name="keyCollection.additionalInfo.instruction.how"
                                        >
                                            <Input>
                                                <InputField
                                                    id="additionalInfo.instruction.how"
                                                    testID="additionalInfo.instruction.how"
                                                    {...register(
                                                        "keyCollection.additionalInfo.instruction.how",
                                                        {
                                                            required: requiredFields.includes(
                                                                "keyCollection.additionalInfo.instruction.how"
                                                            )
                                                                ? "This field is required"
                                                                : false
                                                        }
                                                    )}
                                                />
                                            </Input>
                                        </FormField>
                                        <FormField
                                            isInsideTab
                                            error={
                                                !!formErrors.keyCollection?.additionalInfo
                                                    ?.instruction?.when?.message
                                            }
                                            label="When key should be collected"
                                            name="keyCollection.additionalInfo.instruction.when"
                                        >
                                            <Input>
                                                <InputField
                                                    id="additionalInfo.instruction.when"
                                                    testID="additionalInfo.instruction.when"
                                                    {...register(
                                                        "keyCollection.additionalInfo.instruction.when",
                                                        {
                                                            required: requiredFields.includes(
                                                                "keyCollection.additionalInfo.instruction.when"
                                                            )
                                                                ? "This field is required"
                                                                : false
                                                        }
                                                    )}
                                                />
                                            </Input>
                                        </FormField>
                                    </HStack>
                                </FormGroup>
                                {!isSimple && (
                                    <>
                                        <HStack className="flex-wrap" space="lg">
                                            <FormField label="Tax number">
                                                <Input>
                                                    <InputField
                                                        id="taxNumber"
                                                        testID="taxNumber"
                                                        {...register("taxNumber", {
                                                            required: false
                                                        })}
                                                    />
                                                </Input>
                                            </FormField>
                                            <FormField
                                                isInsideTab
                                                error={formErrors.propertyRating?.message}
                                                label="Property rating"
                                                name="propertyRating"
                                            >
                                                <Input>
                                                    <InputField
                                                        id="propertyRating"
                                                        testID="propertyRating"
                                                        type="number"
                                                        {...register("propertyRating", {
                                                            required: false,
                                                            validate: validateNumber,
                                                            min: {
                                                                value: 1,
                                                                message:
                                                                    "This number should be between 1 and 5"
                                                            },
                                                            max: {
                                                                value: 5,
                                                                message:
                                                                    "This number should be between 1 and 5"
                                                            }
                                                        })}
                                                    />
                                                </Input>
                                            </FormField>
                                            <FormField
                                                isInsideTab
                                                error={formErrors.ratingNumber?.message}
                                                label="Rating number"
                                                name="ratingNumber"
                                            >
                                                <Input>
                                                    <InputField
                                                        id="ratingNumber"
                                                        testID="ratingNumber"
                                                        type="number"
                                                        {...register("ratingNumber", {
                                                            required: false,
                                                            validate: validateNumber
                                                        })}
                                                    />
                                                </Input>
                                            </FormField>
                                        </HStack>
                                        <FormField
                                            isInsideTab
                                            className="w-1/2"
                                            error={formErrors.minimumRentingAge?.message}
                                            label="Minimum renting age"
                                            name="minimumRentingAge"
                                        >
                                            <Input>
                                                <InputField
                                                    id="minimumRentingAge"
                                                    testID="minimumRentingAge"
                                                    type="number"
                                                    {...register("minimumRentingAge", {
                                                        required: false,
                                                        validate: validateNumber
                                                    })}
                                                />
                                            </Input>
                                        </FormField>
                                        <Controller
                                            control={control}
                                            defaultValue={undefined}
                                            name="hostLocation"
                                            render={({field}) => {
                                                return (
                                                    <Checkbox
                                                        isChecked={field.value}
                                                        value="hostLocation"
                                                        onChange={field.onChange}
                                                    >
                                                        <CheckboxIndicator>
                                                            <CheckboxIcon />
                                                        </CheckboxIndicator>
                                                        <CheckboxLabel>Host location</CheckboxLabel>
                                                    </Checkbox>
                                                );
                                            }}
                                        />
                                        <FormField label="Neighborhood overview">
                                            <Controller
                                                control={control}
                                                name="neighborhoodOverview"
                                                render={({field}) => {
                                                    return (
                                                        <TextAreaForm
                                                            languageText={field.value}
                                                            onChange={field.onChange}
                                                        />
                                                    );
                                                }}
                                            />
                                        </FormField>
                                        <FormField label="About">
                                            <Controller
                                                control={control}
                                                name="about"
                                                render={({field}) => {
                                                    return (
                                                        <TextAreaForm
                                                            languageText={field.value}
                                                            onChange={field.onChange}
                                                        />
                                                    );
                                                }}
                                            />
                                        </FormField>
                                    </>
                                )}
                            </VStack>
                        </Tab>
                        <Tab id="images" title="Images">
                            <FormField
                                ref={fieldImagesRef}
                                isInsideTab
                                error={formErrors.images?.message}
                                label="Images"
                                name="images"
                            >
                                <Controller
                                    control={control}
                                    name="images"
                                    render={({field}) => {
                                        return (
                                            <FormUploadFile
                                                ref={uploadImageRef}
                                                canReorder
                                                attachments={field.value}
                                                isNative={false}
                                                maxImages={maxImagesOnListing}
                                                tags={tagOptions}
                                                onChange={handleImagesChange}
                                                onDeleteImages={handleDeleteImages}
                                                onError={handleUploadError}
                                                onUploadImages={handleUploadImages}
                                            />
                                        );
                                    }}
                                    rules={{
                                        validate: value => {
                                            return typeof value !== "undefined"
                                                ? true
                                                : "This field is required";
                                        }
                                    }}
                                />
                            </FormField>
                        </Tab>
                        <Tab id="bedrooms" title="Bedroom Configuration">
                            <BedroomsConfiguration />
                        </Tab>
                        {!isSimple && (
                            <Tab id="policies" title="Policies">
                                <VStack space="lg">
                                    <FormGroup label="Internet policy">
                                        <VStack space="xl">
                                            <Controller
                                                control={control}
                                                defaultValue={false}
                                                name="policy.internetPolicy.accessInternet"
                                                render={({field}) => {
                                                    return (
                                                        <Checkbox
                                                            isChecked={field.value}
                                                            value="accessInternet"
                                                            onChange={field.onChange}
                                                        >
                                                            <CheckboxIndicator>
                                                                <CheckboxIcon />
                                                            </CheckboxIndicator>
                                                            <CheckboxLabel>
                                                                Access internet
                                                            </CheckboxLabel>
                                                        </Checkbox>
                                                    );
                                                }}
                                            />
                                            <VStack className="md:flex-row" space="lg">
                                                <FormField label="Kind of internet">
                                                    <Controller
                                                        control={control}
                                                        name="policy.internetPolicy.kindOfInternet"
                                                        render={({field}) => {
                                                            return (
                                                                <SelectGeneric
                                                                    options={kindOfInternetOptions}
                                                                    placeholder="Kind of internet..."
                                                                    value={field.value}
                                                                    onChange={field.onChange}
                                                                />
                                                            );
                                                        }}
                                                    />
                                                </FormField>
                                                <FormField label="Available Internet">
                                                    <Controller
                                                        control={control}
                                                        name="policy.internetPolicy.availableInternet"
                                                        render={({field}) => {
                                                            return (
                                                                <SelectGeneric
                                                                    options={
                                                                        availableOfInternetOptions
                                                                    }
                                                                    placeholder="Available Internet..."
                                                                    value={field.value}
                                                                    onChange={field.onChange}
                                                                />
                                                            );
                                                        }}
                                                    />
                                                </FormField>
                                                <FormField
                                                    isInsideTab
                                                    error={
                                                        formErrors.policy?.internetPolicy
                                                            ?.chargeInternet?.message
                                                    }
                                                    label="Charge internet"
                                                    name="policy.internetPolicy.chargeInternet"
                                                >
                                                    <Input leftElement={currency}>
                                                        <InputField
                                                            id="chargeInternet"
                                                            placeholder="Charge internet"
                                                            testID="chargeInternet"
                                                            {...register(
                                                                "policy.internetPolicy.chargeInternet",
                                                                {
                                                                    required: false,
                                                                    validate: validateNumber
                                                                }
                                                            )}
                                                        />
                                                    </Input>
                                                </FormField>
                                            </VStack>
                                            {kindOfInternet === "WiFi" && (
                                                <VStack className="md:flex-row" space="lg">
                                                    <FormField
                                                        isInsideTab
                                                        error={
                                                            formErrors.policy?.internetPolicy?.wifi
                                                                ?.ssid?.message
                                                        }
                                                        label="WiFi"
                                                        name="policy.internetPolicy.wifi.ssid"
                                                    >
                                                        <Input>
                                                            <InputField
                                                                id="wifi.ssid"
                                                                placeholder="WiFi network name"
                                                                testID="wifi-ssid"
                                                                {...register(
                                                                    "policy.internetPolicy.wifi.ssid",
                                                                    {
                                                                        required: false
                                                                    }
                                                                )}
                                                            />
                                                        </Input>
                                                    </FormField>
                                                    <FormField
                                                        isInsideTab
                                                        error={
                                                            formErrors.policy?.internetPolicy?.wifi
                                                                ?.password?.message
                                                        }
                                                        label="WiFi Password"
                                                        name="policy.internetPolicy.wifi.password"
                                                    >
                                                        <Input>
                                                            <InputField
                                                                id="wifi.password"
                                                                placeholder="WiFi password"
                                                                testID="wifi-password"
                                                                {...register(
                                                                    "policy.internetPolicy.wifi.password",
                                                                    {
                                                                        required: false
                                                                    }
                                                                )}
                                                            />
                                                        </Input>
                                                    </FormField>
                                                </VStack>
                                            )}
                                        </VStack>
                                    </FormGroup>
                                    <FormGroup label="Parking policy">
                                        <VStack space="xl">
                                            <VStack className="md:flex-row" space="lg">
                                                <Controller
                                                    control={control}
                                                    defaultValue={false}
                                                    name="policy.parkingPolicy.accessParking"
                                                    render={({field}) => {
                                                        return (
                                                            <Checkbox
                                                                isChecked={field.value}
                                                                value="accessParking"
                                                                onChange={field.onChange}
                                                            >
                                                                <CheckboxIndicator>
                                                                    <CheckboxIcon />
                                                                </CheckboxIndicator>
                                                                <CheckboxLabel>
                                                                    Access parking
                                                                </CheckboxLabel>
                                                            </Checkbox>
                                                        );
                                                    }}
                                                />
                                                <Controller
                                                    control={control}
                                                    defaultValue={undefined}
                                                    name="policy.parkingPolicy.privateParking"
                                                    render={({field}) => {
                                                        return (
                                                            <Checkbox
                                                                isChecked={field.value}
                                                                value="privateParking"
                                                                onChange={field.onChange}
                                                            >
                                                                <CheckboxIndicator>
                                                                    <CheckboxIcon />
                                                                </CheckboxIndicator>
                                                                <CheckboxLabel>
                                                                    Private parking
                                                                </CheckboxLabel>
                                                            </Checkbox>
                                                        );
                                                    }}
                                                />
                                            </VStack>
                                            <VStack className="md:flex-row" space="lg">
                                                <FormField label="Located parking">
                                                    <Controller
                                                        control={control}
                                                        name="policy.parkingPolicy.locatedParking"
                                                        render={({field}) => {
                                                            return (
                                                                <SelectGeneric
                                                                    options={locatedParkingOptions}
                                                                    placeholder="Select"
                                                                    value={field.value}
                                                                    onChange={field.onChange}
                                                                />
                                                            );
                                                        }}
                                                    />
                                                </FormField>
                                                <FormField label="Time cost parking">
                                                    <Controller
                                                        control={control}
                                                        name="policy.parkingPolicy.timeCostParking"
                                                        render={({field}) => {
                                                            return (
                                                                <SelectGeneric
                                                                    options={timeCostParkingOptions}
                                                                    placeholder="Select"
                                                                    value={field.value}
                                                                    onChange={field.onChange}
                                                                />
                                                            );
                                                        }}
                                                    />
                                                </FormField>
                                                <FormField
                                                    isInsideTab
                                                    error={
                                                        formErrors.policy?.parkingPolicy
                                                            ?.chargeParking?.message
                                                    }
                                                    label="Charge parking"
                                                    name="policy.parkingPolicy.chargeParking"
                                                >
                                                    <Input leftElement={currency}>
                                                        <InputField
                                                            id="chargeParking"
                                                            placeholder="Charge parking"
                                                            testID="chargeParking"
                                                            {...register(
                                                                "policy.parkingPolicy.chargeParking",
                                                                {
                                                                    required: false,
                                                                    validate: validateNumber
                                                                }
                                                            )}
                                                        />
                                                    </Input>
                                                </FormField>
                                                <FormField label="Reservation parking">
                                                    <Controller
                                                        control={control}
                                                        name="policy.parkingPolicy.necessaryReservationParking"
                                                        render={({field}) => {
                                                            return (
                                                                <SelectGeneric
                                                                    options={
                                                                        reservationParkingOptions
                                                                    }
                                                                    placeholder="Select"
                                                                    value={field.value}
                                                                    onChange={field.onChange}
                                                                />
                                                            );
                                                        }}
                                                    />
                                                </FormField>
                                            </VStack>
                                        </VStack>
                                    </FormGroup>
                                    <FormGroup label="Pet policy">
                                        <VStack className="z-auto" space="xl">
                                            <HStack className="z-auto" space="lg">
                                                <FormField
                                                    isInsideTab
                                                    error={
                                                        formErrors.policy?.petPolicy?.allowedPets
                                                            ?.message
                                                    }
                                                    label="Allowed pet"
                                                    name="policy.petPolicy.allowedPets"
                                                >
                                                    <Controller
                                                        control={control}
                                                        name="policy.petPolicy.allowedPets"
                                                        render={({field}) => {
                                                            return (
                                                                <SelectGeneric
                                                                    options={allowedPetOptions}
                                                                    placeholder="Allowed pet..."
                                                                    value={field.value}
                                                                    onChange={field.onChange}
                                                                />
                                                            );
                                                        }}
                                                        rules={{
                                                            required: requiredFields.includes(
                                                                "policy.petPolicy.allowedPets"
                                                            )
                                                                ? "Please select allowed pets"
                                                                : false
                                                        }}
                                                    />
                                                </FormField>
                                                <FormField
                                                    isInsideTab
                                                    error={
                                                        formErrors.policy?.petPolicy?.chargePets
                                                            ?.message
                                                    }
                                                    label="Charge pet"
                                                    name="policy.petPolicy.chargePets"
                                                >
                                                    <Input leftElement={currency}>
                                                        <InputField
                                                            id="chargePets"
                                                            placeholder="Charge pet"
                                                            testID="chargePets"
                                                            {...register(
                                                                "policy.petPolicy.chargePets",
                                                                {
                                                                    required: false,
                                                                    validate: validateNumber
                                                                }
                                                            )}
                                                        />
                                                    </Input>
                                                </FormField>
                                            </HStack>
                                        </VStack>
                                    </FormGroup>
                                    <FormGroup label="Other policy">
                                        <HStack className="z-auto" space="lg">
                                            <Controller
                                                control={control}
                                                defaultValue={undefined}
                                                name="policy.childrenAllowed"
                                                render={({field}) => {
                                                    return (
                                                        <Checkbox
                                                            isChecked={field.value}
                                                            value="childrenAllowed"
                                                            onChange={field.onChange}
                                                        >
                                                            <CheckboxIndicator>
                                                                <CheckboxIcon />
                                                            </CheckboxIndicator>
                                                            <CheckboxLabel>
                                                                Allow children
                                                            </CheckboxLabel>
                                                        </Checkbox>
                                                    );
                                                }}
                                            />
                                            <Controller
                                                control={control}
                                                defaultValue={undefined}
                                                name="policy.smokingAllowed"
                                                render={({field}) => {
                                                    return (
                                                        <Checkbox
                                                            isChecked={field.value}
                                                            value="smokingAllowed"
                                                            onChange={field.onChange}
                                                        >
                                                            <CheckboxIndicator>
                                                                <CheckboxIcon />
                                                            </CheckboxIndicator>
                                                            <CheckboxLabel>
                                                                Allow smoking
                                                            </CheckboxLabel>
                                                        </Checkbox>
                                                    );
                                                }}
                                            />
                                        </HStack>
                                    </FormGroup>
                                </VStack>
                            </Tab>
                        )}
                        {!isSimple && (
                            <Tab id="attributes" title="Attributes">
                                <AttributesWithQuantity />
                            </Tab>
                        )}
                        {!isSimple && (
                            <Tab id="amenities" title="Amenities">
                                <NearbyAmenitie />
                            </Tab>
                        )}
                        {!isSimple && (
                            <Tab id="fees" title="Fees">
                                <Fees />
                            </Tab>
                        )}
                        {!isSimple && (
                            <Tab id="taxes" title="Taxes">
                                <Taxes />
                            </Tab>
                        )}
                    </Tabs>
                    <HStack className="justify-end" space="sm">
                        <Button disabled={loading} variant="ghost" onPress={onCancel}>
                            <ButtonText>Cancel</ButtonText>
                        </Button>
                        <Button onPress={handleSubmitForm}>
                            {loading && <ButtonSpinner />}
                            {loading ? (
                                <ButtonText>Saving</ButtonText>
                            ) : (
                                <ButtonText>Save</ButtonText>
                            )}
                        </Button>
                    </HStack>
                </VStack>
            </Form>
        </FormProvider>
    );
};

export default memo(ListingForm);
